import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { TabNav } from '../components/layout/TabNav';
import { SideRail } from '../components/layout/SideRail';
import { StatusStrip } from '../components/layout/StatusStrip';
import { Screen } from '../screens';
import { ApiError, wellsApi } from '../lib/api';
import { useAuth } from '../context/auth';
import { useAdmin } from '../context/admin';
import { pickRail, pickTab, SCREENS, useQueryNav, visibleRails } from '../lib/nav';
import type { Dashboard as DashboardData } from '../lib/types';
import { SkeletonDashboard } from '../components/ui/Skeleton';
import { useRtFeed } from '../lib/rt/feed';
import type { RtSample } from '../lib/types';

const REFRESH_MS = 30_000;

/** The portal shell: app bar, rail, tabs, status strip and the active screen. */
export function Dashboard() {
  const { logout } = useAuth();

  // The layout is the role's, and so are the wells: someone put on two wells
  // is offered two, whatever the URL asks for.
  const { access, wells, wellsLoaded } = useAdmin();

  // The sections and tabs this person may open, in the superadmin's order.
  // The dashboard feed belongs to the overview and log screens; someone with
  // only Documents never asks for it.
  const rails = useMemo(
    () => visibleRails(access.layout, access.canOpen),
    [access],
  );
  const wantsDashboard = rails.some((r) =>
    r.tabs.some((t) => SCREENS[t.screen].menu !== 'documents'),
  );
  const { params, set } = useQueryNav();

  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Where you are is read from the URL rather than held in state, so a refresh
  // on RT Logs comes back to RT Logs. An unknown rail or a tab belonging to a
  // different rail falls back to something valid instead of a blank screen.
  const requested = Number(params.get('wellId')) || 0;

  // A superadmin who has not picked a well has not picked a company either,
  // and starts there (pages/CompaniesHome) rather than on somebody's well.
  const acrossCompanies = access.kind === 'superadmin';
  const goCompanies = acrossCompanies && !requested;
  const restricted = access.kind === 'role';
  const well = wells.find((w) => w.id === requested) ?? (restricted || !requested ? wells[0] : undefined);
  // Before roles existed the portal opened well 344 when the URL named none;
  // an account the admin area does not know still gets exactly that.
  const wellId = restricted ? (well?.id ?? 0) : requested || well?.id || 344;

  // A well created in the admin area has no data behind it yet.
  const localWell = well?.local === true && well.id === wellId;
  const noWell = restricted && wellsLoaded && wellId === 0;
  const rail = pickRail(rails, params.get('rail'));
  const tab = rail && pickTab(rail, params.get('tab'));
  const screen = tab && SCREENS[tab.screen];

  const selectWell = useCallback(
    (id: number) => set({ wellId: String(id) }, { replace: true }),
    [set],
  );

  // Changing rail carries its first tab, and drops the document-tree state:
  // those keys mean nothing outside Documents, and a stale folder in the URL
  // would be restored the next time somebody opened that section.
  // A section's only tab is not written into the URL: there is no tab row to
  // match it, and Documents links have never carried one.
  const selectRail = useCallback(
    (id: string) => {
      const next = rails.find((r) => r.id === id);
      const first = next && next.tabs.length > 1 ? next.tabs[0].id : null;
      set({ rail: id, tab: first, folder: null, page: null });
    },
    [rails, set],
  );

  const selectTab = useCallback((next: string) => set({ tab: next }), [set]);

  useEffect(() => {
    if (goCompanies || !wantsDashboard || wellId === 0 || localWell) return;
    let cancelled = false;

    async function load() {
      try {
        const d = await wellsApi.dashboard(wellId);
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          logout();
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Could not load dashboard data.');
      }
    }

    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [wellId, logout, wantsDashboard, localWell, goCompanies]);

  const live = screen?.live ?? false;

  // Nothing to draw for a role with no well, or for a well with no data yet;
  // what the last well left behind must not stand in for it.
  const blank = noWell || localWell;
  const shown = blank ? null : data;

  // A well with a real-time feed keeps the strip moving between the
  // dashboard's 30-second refreshes; the RT screens share this connection.
  const rt = useRtFeed(shown?.liveFeed && live ? wellId : null);

  if (goCompanies) return <Navigate to="/web/Companies" replace />;

  // Inside a company the picker offers that company's wells, not everyone's.
  const pickable = acrossCompanies && well ? wells.filter((w) => w.companyId === well.companyId) : wells;

  return (
    <div className="app">
      <Header context="well" wells={pickable} activeWellId={wellId} onSelectWell={selectWell} />
      <TabNav
        tabs={rail && rail.tabs.length > 1 ? rail.tabs : []}
        active={tab?.id ?? ''}
        onChange={selectTab}
      />

      <div className="workspace">
        <SideRail rails={rails} active={rail?.id ?? ''} onChange={selectRail} />

        <main className="content">
          {error && !blank && (
            <div className="alert alert--page" role="alert">
              {error}
            </div>
          )}

          {rails.length === 0 && access.admin.users && (
            <div className="content-loading">
              Your role looks after people, not wells, so there is no well to open here.{' '}
              <Link to="/web/Admin">Open Administration</Link>
            </div>
          )}

          {rails.length === 0 && !access.admin.users && (
            <div className="content-loading">
              No section of the portal is open to your account yet. Ask a superadmin to give you
              access.
            </div>
          )}

          {rails.length > 0 && noWell && (
            <div className="content-loading">
              No well has been given to your account yet. Ask your Well Admin to put you on one.
            </div>
          )}

          {rails.length > 0 && localWell && (
            <div className="content-loading">
              {well?.name} was created in Administration and exists only in this browser for now.
              Its data appears here once the API stores wells.
            </div>
          )}

          {wantsDashboard && !blank && !data && !error && (
            <SkeletonDashboard />
          )}

          {shown && screen?.strip && (
            <StatusStrip
              currentActivity={live ? (rt.latest?.activity ?? shown.currentActivity) : 'N/A'}
              tiles={
                live
                  ? shown.tiles.map((t) =>
                      rt.latest && t.label === 'Hole Depth'
                        ? { ...t, value: rt.latest.holeDepth.toFixed(2) }
                        : rt.latest && t.label === 'Bit Depth'
                          ? { ...t, value: rt.latest.bitDepth.toFixed(2) }
                          : t,
                    )
                  : shown.tiles.map((t) => ({ ...t, value: 'N/A', unit: t.unit && 'N/A' }))
              }
              rigStates={
                !live
                  ? shown.rigStates.map((s) => ({ ...s, active: false }))
                  : rt.latest
                    ? rigStatesOf(rt.latest)
                    : shown.rigStates
              }
              lastData={
                !live ? 'N/A' : rt.latest ? new Date(rt.latest.t).toLocaleString(undefined, { hour12: false }) : shown.lastData
              }
            />
          )}

          {tab && !blank && (
            <Screen
              screen={tab.screen}
              tab={tab}
              data={shown}
              wellId={wellId}
              wellName={shown?.well.name ?? well?.name ?? 'Well'}
            />
          )}
        </main>
      </div>
    </div>
  );
}

/** The strip's rig-state lamps, read from a real-time sample. */
function rigStatesOf(s: RtSample) {
  return [
    { label: 'Pumping', active: s.flowIn > 50 },
    { label: 'Rotating', active: s.rpm > 5 },
    { label: 'On Bottom', active: s.holeDepth - s.bitDepth < 0.3 && s.wob > 2 },
    { label: 'In Slips', active: s.activity === 'In-slip' },
  ];
}
